import { useEffect, useReducer, useRef, useState } from 'react';

import {
  canSubmitScreenerAnswer,
  RESOLVING_SCREENER_FLOW,
  resolveSingleSelectScreenerAnswer,
  SCREENER_STATUSES,
  screenerFlowReducer,
  serializeScreenerAnswer,
  type ScreenerFlowEvent,
  type ScreenerFlowState,
  type ScreenerSession,
  type ScreenerStartLatch,
} from '@/shared-responder-v2-core';

import {
  IN_MEMORY_INSIGHT_STORE,
  type ResponderInsightStore,
} from './shared-responder-v2-insight-session.helpers';
import {
  resolveScreenerStart,
  toScreenerKeyRejectionEvent,
} from './shared-responder-v2-screener-latch.helpers';

export type ScreenerAnswerFailureOutcome =
  | { readonly kind: 'restart' }
  | { readonly kind: 'dispatch'; readonly event: ScreenerFlowEvent };

interface UseScreenerFlowInput {
  readonly projectId: string;
  readonly hasScreener: boolean;
  /**
   * Holds the start while the caller cannot yet decide whether the responder
   * may take the screener here at all (the device gate before the viewport is
   * known, or while it blocks). The flow stays `Resolving` and starts once
   * this clears; deciding `hasScreener: false` instead would bypass for good.
   */
  readonly isDeferred?: boolean;
  readonly startSession: () => Promise<ScreenerSession>;
  readonly answerQuestion: (input: {
    readonly sessionId: string;
    readonly answer: string;
  }) => Promise<ScreenerFlowEvent>;
  readonly onAnswerFailure: (error: unknown) => ScreenerAnswerFailureOutcome;
  readonly onAnswerEvent?: (event: ScreenerFlowEvent) => void;
  readonly startFailureLogMessage?: string;
  readonly insightStore?: ResponderInsightStore;
}

interface UseScreenerFlowResult {
  readonly state: ScreenerFlowState;
  readonly onToggleOption: (value: string) => void;
  readonly onContinue: () => void;
  readonly onSelectAndContinue: (value: string) => void;
  readonly onKeyRejected: () => void;
}

export function useScreenerFlow({
  projectId,
  hasScreener,
  isDeferred = false,
  startSession,
  answerQuestion,
  onAnswerFailure,
  onAnswerEvent,
  startFailureLogMessage,
  insightStore = IN_MEMORY_INSIGHT_STORE,
}: UseScreenerFlowInput): UseScreenerFlowResult {
  const latchRef = useRef<ScreenerStartLatch>({
    key: null,
    isInFlight: false,
    hasResolved: false,
  });

  const [generation, setGeneration] = useState(0);
  const [resolvedProjectId, setResolvedProjectId] = useState(projectId);

  const [state, dispatch] = useReducer(
    screenerFlowReducer,
    RESOLVING_SCREENER_FLOW
  );

  if (projectId !== resolvedProjectId) {
    setResolvedProjectId(projectId);
    dispatch({ type: 'restarted' });
  }

  useEffect(() => {
    void resolveScreenerStart(
      latchRef.current,
      {
        key: `${projectId}:${generation}`,
        hasScreener,
        isDeferred,
        storedInsightId: insightStore.read(projectId),
      },
      startSession,
      dispatch,
      startFailureLogMessage
    );
  }, [
    generation,
    hasScreener,
    insightStore,
    isDeferred,
    projectId,
    startFailureLogMessage,
    startSession,
  ]);

  function restart(): void {
    dispatch({ type: 'restarted' });
    setGeneration((current) => current + 1);
  }

  function handleKeyRejected(): void {
    const event = toScreenerKeyRejectionEvent(state);

    if (event.type === 'restarted') {
      restart();
      return;
    }
    dispatch(event);
  }

  async function submitAnswer(
    selectedValues: readonly string[]
  ): Promise<void> {
    if (
      state.status !== SCREENER_STATUSES.Active ||
      state.isSubmitting ||
      !canSubmitScreenerAnswer(state.question, selectedValues)
    ) {
      return;
    }

    dispatch({ type: 'submitStarted' });

    try {
      const event = await answerQuestion({
        sessionId: state.sessionId,
        answer: serializeScreenerAnswer(selectedValues),
      });

      onAnswerEvent?.(event);
      dispatch(event);
    } catch (error) {
      const outcome = onAnswerFailure(error);

      if (outcome.kind === 'restart') {
        restart();
        return;
      }
      dispatch(outcome.event);
    }
  }

  return {
    state,
    onToggleOption: (value: string) =>
      dispatch({ type: 'optionToggled', value }),
    onContinue: () => {
      if (state.status === SCREENER_STATUSES.Active) {
        void submitAnswer(state.selectedValues);
      }
    },
    onSelectAndContinue: (value: string) => {
      const selectedValues = resolveSingleSelectScreenerAnswer(state, value);

      if (selectedValues === null) {
        return;
      }

      dispatch({ type: 'optionToggled', value });
      void submitAnswer(selectedValues);
    },
    onKeyRejected: handleKeyRejected,
  };
}
